<template>
  <UiHorizontalScroll class="delivery-info">
    <UiUiButtonIco
      type="secondary"
      icon="info-circle"
      size="medium"
      stroke="#363ef8"
      @click="$emit('open-info')"
    />
    <div class="delivery-item">
      <UiIcon class="delivery-icon" display="inline-flex" name="delivery" />
      <span
        v-if="delivery.dayText"
        class="delivery-text delivery-text__margin body-m-regular"
        >{{ delivery.dayText }}</span
      >
      <span v-if="delivery.dayText" class="delivery-point">•</span>
      <span
        v-if="delivery.cost"
        class="delivery-cost body-m-regular"
        :class="{ 'delivery-cost__green': delivery.cost === '0₽' }"
        >{{ delivery.cost }}</span
      >
    </div>
    <div class="delivery-item">
      <UiIcon class="delivery-icon" display="inline-flex" name="payment" />
      <span class="delivery-text body-m-regular">{{ payment }}</span>
    </div>
    <div class="delivery-item">
      <UiIcon class="delivery-icon" display="inline-flex" name="locations" />
      <span class="delivery-text body-m-regular">{{ pickup }}</span>
    </div>
  </UiHorizontalScroll>
</template>

<script lang="ts" setup>
import {
  getDeliveryDateRange,
  getWeekDay,
  getFutureDay,
} from '~/utils/dateUtils';

defineEmits(['open-info']);
const props = defineProps({
  model: {
    type: Object,
    required: true,
  },
});

// текст самовывоза
const pickup = computed(() => {
  if (props.model?.pickup?.description || props.model?.pickup?.options?.length)
    return `Самовывоз есть`;
  return `Самовывоз нет`;
});

// текст доставки
const delivery = computed<{ cost: string; dayText: string }>(() => {
  if (!props.model.delivery) return { cost: '', dayText: '' };

  // через сколько дней минимально доставка
  const day: number | null =
    props.model.delivery.options && props.model?.delivery?.options?.length > 0
      ? props.model.delivery.options.reduce(
          (day: number | null, item: { days: string | undefined }) => {
            if (item.days) {
              const interval = item.days.split(/\D/).map((d) => Number(d));
              if (
                interval &&
                interval.length &&
                (day === null || interval[0] < day)
              )
                return interval[0];
            }
            return day;
          },
          null
        )
      : null;

  // рассчет минимальной доставки из delivery.options
  const getDeliveryOptionsCost = (arr: { cost: number | null }[]) => {
    const min = Math.min(...arr.map((item) => (item.cost ? item.cost : 0)));
    return min ? `от\u00A0${min}\u00A0₽` : min === 0 ? '0₽' : 'есть';
  };

  const getCost = () => {
    if (props.model.delivery.cost === null) return 'Есть';
    if (!props.model.delivery.cost)
      return props.model.delivery.options && props.model.delivery.options.length
        ? getDeliveryOptionsCost(props.model.delivery.options)
        : 'есть';
    return `${props.model.delivery.fixed ? '' : 'от\u00A0'}${
      props.model.delivery.cost
    }\u00A0₽`;
  };

  // цена доставки
  const cost: string = getCost();

  // текст доставки по дате
  let dayText = '';
  if (day !== null && day < 3) {
    dayText = `${getFutureDay(day)}`;
  } else if (day !== null) {
    const date = new Date();
    date.setDate(date.getDate() + day);
    dayText = `в ${getWeekDay(date)}, ${getDeliveryDateRange(
      date.toDateString()
    ).toLowerCase()}`;
  }

  return {
    dayText,
    cost,
  };
});

// Безнал
const payment = computed(() => {
  if (
    props.model.shop_info?.accepts_card &&
    props.model.shop_info?.accepts_cash
  )
    return 'Наличными и картой';
  if (props.model.shop_info?.accepts_card) return 'Оплата картой';
  if (props.model.shop_info?.accepts_cash) return 'Оплата наличными';
  const payment = props.model.shop_info?.payment_options || '';
  return !payment || payment === 'нет' ? 'Оплата наличными' : '';
});
</script>
<style scoped lang="scss">
.delivery-info {
  display: flex;
  @include md-block {
    display: none;
  }

  button,
  .delivery-item {
    margin-right: 4px;
  }

  .delivery-item {
    padding: 8px 10px;
    border-radius: 8px;
    background: $background-secondary-default;
    display: flex;
    align-items: center;
    -webkit-text-size-adjust: 100%;
    white-space: nowrap;
  }

  .delivery-point {
    margin-right: 4px;
    color: $achromatic-disabled;
  }

  .delivery-text__margin {
    margin-right: 4px;
  }

  .delivery-cost {
    &__green {
      color: #3fae42;
    }
  }

  .delivery-icon {
    margin-right: 6px;
  }
}
</style>
